import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AudioTourCardProps } from "../../types/screensTypes";
import Icon from "../common/Icon";
import styles from "./AudiotourCard.module.css";

const AudioTourCard: React.FC<AudioTourCardProps> = ({
    picture,
    title,
    duration,
    start,
    trackId,
    isActive,
    loading,
    activateProduct,
    deactivateProduct,
    deleteProduct,
    cloneProduct,
}) => {
    const navigate = useNavigate();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const toggleMenu = () => {
        setIsMenuOpen((prev) => !prev);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setIsMenuOpen(false);
        }
    };

    useEffect(() => {
        if (isMenuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isMenuOpen]);

    return (
        <div
            className={[styles.card, isMenuOpen ? styles.cardMenuOpen : ""].filter(Boolean).join(" ")}
            ref={menuRef}
            data-rw-card="audiotour"
        >
            <div className={styles.imageContainer}>
                <img
                    src={picture && picture !== "" ? picture : "https://via.placeholder.com/300x150"}
                    alt=""
                    className={styles.tourImage}
                />
            </div>
            <div className={styles.content}>
                <h3 className={styles.cardTitle}>{title}</h3>
                <div className={styles.meta}>
                    <div className={styles.metaItem}>
                        <Icon name="ic-clock" className={`ic ${styles.metaIcon}`} size={14} />
                        {t("durationWithSemicolon")} {duration} min
                    </div>
                    <div className={styles.metaItem}>
                        <Icon name="ic-pin" className={`ic ${styles.metaIcon}`} size={14} />
                        {t("startWithSemicolon")} {start}
                    </div>
                </div>
            </div>
            <div className={styles.menu}>
                <button
                    type="button"
                    className={[styles.moreBtn, isMenuOpen ? styles.moreBtnOpen : ""].filter(Boolean).join(" ")}
                    onClick={toggleMenu}
                    aria-expanded={isMenuOpen}
                >
                    {t("seeDetails")}
                    <Icon name="ic-chevron-down" className={`ic ${styles.moreIcon}`} size={13} />
                </button>
                {isMenuOpen && (
                    <div className={styles.dropdown}>
                        <button disabled={loading} onClick={cloneProduct} className={styles.menuItem}>
                            {loading ? t("loading") : t("cloneProduct")}
                        </button>
                        <button
                            className={styles.menuItem}
                            onClick={() => navigate("/create/tour/" + trackId + "/category")}
                        >
                            {t("editProduct")}
                        </button>
                        <button
                            disabled={loading}
                            onClick={isActive ? deactivateProduct : activateProduct}
                            className={`${styles.menuItem} ${styles.menuItemSep}`}
                        >
                            {loading ? t("loading") : isActive ? t("deactivate") : t("activate")}
                        </button>
                        <button
                            disabled={loading}
                            onClick={() => {
                                const confirmed = window.confirm(t("deleteConfirmation"));
                                if (confirmed) {
                                    deleteProduct();
                                }
                            }}
                            className={`${styles.menuItem} ${styles.menuItemDel}`}
                        >
                            {loading ? t("loading") : t("delete")}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AudioTourCard;
